import { API_BASE } from '../config/api';
import { optimizeImageFile } from '../utils/imageOptimization';

export const initialForm = {
  personal: {
    fullName: '',
    mobile: '',
    email: '',
    gender: '',
    dob: '',
    city: '',
    country: '',
  },
  skills: {
    primaryRole: 'Player',
    battingStyle: '',
    bowlingStyle: '',
    skillTags: ['Player'],
  },
  bio: {
    intro: '',
    experience: '',
    strengths: '',
    nickname: '',
  },
  team: {
    affiliationType: 'organization',
    affiliationDetails: {
      name: '',
      role: '',
      department: '',
    },
  },
  media: {
    profilePhoto: '',
    coverImage: '',
  },
  addOns: {
    careerHighlightsEnabled: false,
    achievementsEnabled: false,
    milestonesEnabled: false,
    playerBadgesEnabled: false,
    levelBadgesEnabled: false,
    awardsEnabled: false,
    matchParticipationEnabled: false,
    joinTournamentsEnabled: false,
    joinTeamsEnabled: false,
    mediaEnabled: false,
  },
  highlights: [],
  achievements: [],
  milestones: [],
  awards: [],
  badges: [],
  level: { levelName: 'Rookie', points: 0, updatedAt: '' },
  matchParticipation: {
    showHistory: false,
    summary: { matches: '', runs: '', wickets: '', best: '', lastMatchDate: '' },
  },
  preferences: {
    openToTournaments: false,
    openToTeams: false,
  },
  mediaGallery: [],
  extraFeatures: [],

};

const buildHeaders = (token) => {
  const headers = { 'Content-Type': 'application/json' };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const buildAuthHeaders = (token) => {
  if (!token) return undefined;
  return { Authorization: `Bearer ${token}` };
};

const isFileUpload = (value) =>
  typeof File !== 'undefined' && value instanceof File;

const buildProfilePayload = (formData) => {
  const { stats, ...rest } = formData || {};

  return {
  ...rest,
  media: {
    ...(formData.media || {}),
    profilePhoto: isFileUpload(formData.media?.profilePhoto) ? '' : formData.media?.profilePhoto || '',
    coverImage: isFileUpload(formData.media?.coverImage) ? '' : formData.media?.coverImage || '',
  },
  };
};

const buildMultipartPayload = async (formData) => {
  const payload = buildProfilePayload(formData);
  const formPayload = new FormData();
  formPayload.append('payload', JSON.stringify(payload));

  if (isFileUpload(formData.media?.profilePhoto)) {
    formPayload.append('profilePhoto', await optimizeImageFile(formData.media.profilePhoto, 'AVATAR'));
  }

  if (isFileUpload(formData.media?.coverImage)) {
    formPayload.append('coverImage', await optimizeImageFile(formData.media.coverImage, 'BANNER'));
  }

  return formPayload;
};

const hasProfileFiles = (formData) =>
  isFileUpload(formData.media?.profilePhoto) || isFileUpload(formData.media?.coverImage);

export const fetchMessage = async () => {
  const response = await fetch(`${API_BASE}/api/players/message`);
  if (!response.ok) {
    throw new Error('Failed to fetch backend message');
  }
  return response.json();
};

export const fetchPlayers = async (token) => {
  const response = await fetch(`${API_BASE}/api/players`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to fetch existing players.');
  }

  return Array.isArray(data) ? data : data.players || [];
};

export const fetchPlayerDiscovery = async (token, limit = 5) => {
  const response = await fetch(`${API_BASE}/api/players/discover?limit=${encodeURIComponent(limit)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Unable to discover players.');
  return data;
};

export const searchPlayerDiscovery = async (query, token, limit = 20) => {
  const response = await fetch(`${API_BASE}/api/players/search?q=${encodeURIComponent(query || '')}&limit=${encodeURIComponent(limit)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Unable to search players.');
  return data;
};

export const fetchPlayerById = async (playerId, token) => {
  const response = await fetch(`${API_BASE}/api/players/${playerId}?_ts=${Date.now()}`, {
    headers: token ? { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' } : { 'Cache-Control': 'no-cache' },
    cache: 'no-store',
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to fetch player details.');
  }

  return data?.player || data;
};

export const fetchAdminPlayers = async (token) => {
  if (!token) {
    throw new Error('Super admin authorization required.');
  }

  const response = await fetch(`${API_BASE}/api/players/admin`, {
    headers: buildHeaders(token),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to fetch existing players.');
  }

  return Array.isArray(data) ? data : data.players || [];
};

export const fetchAdminPlayerById = async (playerId, token) => {
  if (!token) {
    throw new Error('Super admin authorization required.');
  }

  const response = await fetch(`${API_BASE}/api/players/admin/${playerId}`, {
    headers: buildHeaders(token),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to fetch player details.');
  }

  return data;
};

export const createPlayerProfile = async (formData, token) => {
  const useMultipart = hasProfileFiles(formData);
  const response = await fetch(`${API_BASE}/api/players/profile`, {
    method: 'POST',
    headers: useMultipart ? buildAuthHeaders(token) : buildHeaders(token),
    body: useMultipart ? await buildMultipartPayload(formData) : JSON.stringify(buildProfilePayload(formData)),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to create player profile.' }));
    throw new Error(errorData.error || 'Failed to create player profile.');
  }

  return response.json();
};

export const updatePlayerProfile = async (formData, token) => {
  const useMultipart = hasProfileFiles(formData);
  const endpoint = `${API_BASE}/api/players/profile`;

  const body = useMultipart
    ? await buildMultipartPayload(formData)
    : JSON.stringify(buildProfilePayload(formData));

  const response = await fetch(endpoint, {
    method: 'PUT',
    headers: useMultipart ? buildAuthHeaders(token) : buildHeaders(token),
    body,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to update player profile.' }));
    throw new Error(errorData.error || 'Failed to update player profile.');
  }

  const data = await response.json();
  return data?.player ? data : { player: data };
};


export const recomputePlayerStatsById = async (playerId, token) => {
  if (!playerId) {
    throw new Error('playerId is required to recompute stats.');
  }

  const response = await fetch(`${API_BASE}/api/players/${playerId}/recompute-stats`, {
    method: 'POST',
    headers: buildHeaders(token),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to recompute player stats.');
  }

  return data;
};

export const fetchMyProfile = async (token) => {
  const response = await fetch(`${API_BASE}/api/players/profile/me?_ts=${Date.now()}`, {
    headers: token ? { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' } : { 'Cache-Control': 'no-cache' },
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to fetch player profile.' }));
    throw new Error(errorData.error || 'Failed to fetch player profile.');
  }

  const data = await response.json();
  return data;
};

export const recomputePlayerDotBalls = async ({ playerId, tournamentIds = [] }, token) => {
  if (!playerId) {
    throw new Error('playerId is required to recompute dot balls.');
  }

  const response = await fetch(`${API_BASE}/api/temp/recompute-player-dot-balls`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ playerId, tournamentIds }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to recompute dot balls.');
  }

  return data;
};
